import React, { useCallback, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const gymPhotos = [
  {
    id: 1,
    url: "https://customer-assets-4nw71qhi.emergentagent.net/job_nex-fitness/artifacts/chxtrewi_IMG_4780.JPG%20%287%29.webp",
    alt: "Equipamiento NEX Level Fitness 1"
  },
  {
    id: 2,
    url: "https://customer-assets-4nw71qhi.emergentagent.net/job_nex-fitness/artifacts/yt8h7w63_IMG_4778.JPG%20%283%29.webp",
    alt: "Equipamiento NEX Level Fitness 2"
  },
  {
    id: 3,
    url: "https://customer-assets-4nw71qhi.emergentagent.net/job_nex-fitness/artifacts/pt7galfp_IMG_4777.JPG%20%281%29.webp",
    alt: "Equipamiento NEX Level Fitness 3"
  },
  {
    id: 4,
    url: "https://customer-assets-4nw71qhi.emergentagent.net/job_nex-fitness/artifacts/z831eq9f_IMG_4781.JPG%20%282%29.webp",
    alt: "Equipamiento NEX Level Fitness 4"
  },
  {
    id: 5,
    url: "https://customer-assets-4nw71qhi.emergentagent.net/job_nex-fitness/artifacts/sldjm4ue_IMG_4779.JPG%20%281%29.webp",
    alt: "Equipamiento NEX Level Fitness 5"
  }
];

export const GymCarousel = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true,
      align: 'center',
      skipSnaps: false
    },
    [Autoplay({ delay: 4000, stopOnInteraction: false })]
  );

  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback((index) => {
    if (emblaApi) emblaApi.scrollTo(index);
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  return (
    <div className="relative w-full max-w-6xl mx-auto">
      {/* Carousel Container */}
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {gymPhotos.map((photo) => (
            <div
              key={photo.id}
              className="flex-[0_0_100%] min-w-0 md:flex-[0_0_80%] lg:flex-[0_0_70%] px-4"
            >
              <div className="relative group">
                {/* Image with hover effect */}
                <div className="overflow-hidden rounded-2xl shadow-2xl shadow-blue-500/20">
                  <img
                    src={photo.url}
                    alt={photo.alt}
                    className="w-full h-[400px] md:h-[500px] object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                
                {/* Subtle gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-2xl pointer-events-none" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={scrollPrev}
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white p-3 rounded-full shadow-2xl shadow-blue-500/50 transition-all duration-300 hover:scale-110 z-10"
        aria-label="Previous slide"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>

      <button
        onClick={scrollNext}
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white p-3 rounded-full shadow-2xl shadow-blue-500/50 transition-all duration-300 hover:scale-110 z-10"
        aria-label="Next slide"
      >
        <ChevronRight className="h-6 w-6" />
      </button>

      {/* Dot Indicators */}
      <div className="flex justify-center gap-2 mt-6">
        {gymPhotos.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollTo(index)}
            className={`transition-all duration-300 rounded-full ${
              index === selectedIndex
                ? 'w-8 h-3 bg-gradient-to-r from-blue-600 to-sky-500 shadow-lg shadow-blue-500/50'
                : 'w-3 h-3 bg-gray-600 hover:bg-gray-500'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};
